import { API } from "@memberpoint/mp-frontend-core";
import {
  DataTableStateChangeEvent,
  DataTableFilterManager
} from "@memberpoint/mp-components";
import { FilterParser } from "@memberpoint/mp-frontend-core";
import CloneDeep from "lodash/cloneDeep";

const APIUrlBuilder = API.APIUrlBuilder;

export default {
  data() {
    return {
      urlBuilder: null,
      filterUnwatch: null,

      /**
       * @type {FilterParser}
       */
      _dataTableFilters: null
    };
  },

  watch: {
    /**
     * The `filters` either exist or not, but if it does then we
     * should rebuild the urlBuilder whenever the filters are updated.
     */
    filters: {
      handler(nv) {
        this.urlBuilder = this.buildUrlBuilderFromDataTableFilter(nv);
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    /**
     * Returns the APIUrlBuilder instance from the data table state.
     *
     * @param state
     * @param {APIUrlBuilder|null} urlBuilder
     * @return {APIUrlBuilder}
     */
    buildUrlBuilderFromDataTableState(state, urlBuilder = null) {
      if (!(state instanceof DataTableStateChangeEvent)) {
        throw new Error(
            "state must be an instance of DataTableStateChangeEvent"
        );
      }

      if (urlBuilder === null) {
        urlBuilder = new APIUrlBuilder();
      }

      // add the filters.
      this.addFiltersToUrlBuilder(urlBuilder, state.getSelectedFilters());

      // set the sorting.
      state.getSort().forEach(sort => {
        urlBuilder.setSort(sort.column, sort.asc);
      });

      // set the pagination.
      urlBuilder.setPagination(
          state.getPaginationLimit(),
          state.getPaginationOffset()
      );

      return urlBuilder;
    },

    /**
     * Returns an instance of APIUrlBuilder with the added filters.
     *
     * @param filters
     * @param {APIUrlBuilder|null} urlBuilder
     * @return {APIUrlBuilder}
     */
    buildUrlBuilderFromDataTableFilter(filters, urlBuilder = null) {
      if (urlBuilder === null) {
        urlBuilder = new APIUrlBuilder();
      }

      this.addFiltersToUrlBuilder(urlBuilder, filters);

      return urlBuilder;
    },

    /**
     * Add the filters to URLBuilder instance.
     *
     * @param urlBuilder
     * @param filters
     */
    addFiltersToUrlBuilder(urlBuilder, filters) {
      const groupValues = {};

      for (const key in filters) {
        if (filters.hasOwnProperty(key)) {
          const value = DataTableFilterManager.encodeUrl(filters, key);

          if (value === null) {
            continue;
          }

          const filter = filters[key];

          // if their is a `group` property we should add those separately
          // a group filter should use the same group name as key for all the filter values.
          if (
              filter.hasOwnProperty("group") &&
              typeof filter.group === "string" &&
              filter.group.trim().length > 0
          ) {
            if (!Array.isArray(groupValues[filter.group])) {
              groupValues[filter.group] = [];
            }

            groupValues[filter.group].push(value);
          } else {
            urlBuilder.addFilter(key, value);
          }
        }
      }

      // add filters from group
      for (const key in groupValues) {
        if (groupValues.hasOwnProperty(key)) {
          urlBuilder.addFilter(key, groupValues[key]);
        }
      }
    },

    /**
     * Process a filter update and rebuild the current route from
     * the new filters query
     *
     * @param {object|null} filters
     */
    DataTableFiltersRouterUpdate(filters = null) {
      // Clone the existing query string
      let query = Object.assign({}, this.$route.query);

      let output = {};

      // Load up the filters
      if (filters === null) {
        output = this._dataTableFilters.exportQuery(true);
      } else {
        output = this.createFilterParser(filters).exportQuery(true);
      }

      // Loop through all the filters and build up the query string
      for (const i in output) {
        if (output.hasOwnProperty(i)) {
          // Remove Existing filters
          if (query.hasOwnProperty(`filters[${i}]`)) {
            delete query[`filters[${i}]`];
          }

          // Add new filters if they aren't blank
          if (output[i] !== "" && output[i] !== null) {
            query[`filters[${i}]`] = output[i];
          }
        }
      }

      // Build up the route from the existing route
      let route = {
        name: this.$route.name,
        query,
        params: Object.assign({}, this.$route.params)
      };

      // Update the router
      this.$router.push(route).catch(() => {});
    },

    /**
     * Returns a new instance of FilterParser.
     *
     * @param filters
     * @return {FilterParser}
     */
    createFilterParser(filters) {
      return new FilterParser(CloneDeep(filters));
    },

    /**
     * Updates the given filters selected with current route url.
     *
     * @param {Object} filters
     * @return {FilterParser}
     */
    parseFiltersFromUrl(filters) {
      const filterParser = this.createFilterParser(filters);
      filterParser.parse(window.location.search);

      return filterParser;
    },

    /**
     * Initialize filters for this object.
     * @param filters {Object} The imported filters object
     * @returns {FilterParser} a pointer to the The parsed filters object
     */
    DataTableFiltersInit(filters) {
      this._dataTableFilters = this.parseFiltersFromRoute(filters);
      this._dataTableFilters.onSelect(() => {
        this.DataTableFiltersRouterUpdate();
      });
      return this._dataTableFilters;
    }
  }
};
